import { useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Slider,
  Button,
  Chip,
  Alert,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecordOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import { useActiveProject } from '../lib/activeProject.js';
import { useMe } from '../api/hooks.js';

type RuleMode = 'off' | 'allow' | 'deny';
type RuleKind = 'user' | 'ip';

interface Rule {
  id: string;
  kind: RuleKind;
  value: string;
  note: string | null;
  createdAt: string;
}

interface ProjectSettings {
  id: string;
  name: string;
  samplingRate: number;
  samplingMode: 'user' | 'session';
  userRuleMode: RuleMode;
  ipRuleMode: RuleMode;
}

/**
 * Who gets recorded, per project.
 *
 * The screen is arranged so the destructive combination is impossible to miss:
 * "record only those listed" with an empty list records nothing at all, which
 * looks identical to a broken SDK from the outside. It is called out where the
 * mode is chosen rather than in a help page.
 */
export function RecordingRulesPage() {
  const all = useT();
  const t = all.recording;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { selected, projectId, setProjectId, projects, loading } = useActiveProject(orgId);
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<RuleKind, { value: string; note: string }>>({
    user: { value: '', note: '' },
    ip: { value: '', note: '' },
  });
  const [error, setError] = useState<string | null>(null);

  const base = `/v1/orgs/${orgId}/projects/${projectId}`;

  const settings = useQuery({
    enabled: !!orgId && !!projectId,
    queryKey: ['project-settings', projectId],
    queryFn: async () => (await api.get(base)).data as ProjectSettings,
  });

  const rules = useQuery({
    enabled: !!orgId && !!projectId,
    queryKey: ['recording-rules', projectId],
    queryFn: async () => (await api.get(`${base}/recording-rules`)).data.data as Rule[],
  });

  const patch = useMutation({
    mutationFn: async (body: Partial<ProjectSettings>) => (await api.patch(base, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-settings', projectId] }),
  });

  const addRule = useMutation({
    mutationFn: async (body: { kind: RuleKind; value: string; note?: string }) =>
      (await api.post(`${base}/recording-rules`, body)).data,
    onSuccess: (_d, body) => {
      setDraft((s) => ({ ...s, [body.kind]: { value: '', note: '' } }));
      setError(null);
      qc.invalidateQueries({ queryKey: ['recording-rules', projectId] });
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) =>
      setError(err.response?.data?.error?.message ?? 'Could not add that entry.'),
  });

  const removeRule = useMutation({
    mutationFn: async (id: string) => (await api.delete(`${base}/recording-rules/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recording-rules', projectId] }),
  });

  if (!loading && projects.length === 0) return <Alert severity="info">{t.noProject}</Alert>;

  const s = settings.data;
  const listOf = (kind: RuleKind) => (rules.data ?? []).filter((r) => r.kind === kind);

  const modeField = (value: RuleMode, onChange: (next: RuleMode) => void) => (
    <TextField
      select
      size="small"
      label={t.rules.mode}
      value={value}
      onChange={(e) => onChange(e.target.value as RuleMode)}
      sx={{ minWidth: 300 }}
    >
      <MenuItem value="off">{t.rules.modeOff}</MenuItem>
      <MenuItem value="allow">{t.rules.modeAllow}</MenuItem>
      <MenuItem value="deny">{t.rules.modeDeny}</MenuItem>
    </TextField>
  );

  const ruleSection = (kind: RuleKind, heading: string, intro: string, mode: RuleMode) => {
    const list = listOf(kind);
    return (
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h6">{heading}</Typography>
          {list.length > 0 && <Chip size="small" label={t.rules.count(list.length)} />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
          {intro}
        </Typography>

        {modeField(mode, (next) =>
          patch.mutate(kind === 'user' ? { userRuleMode: next } : { ipRuleMode: next }),
        )}

        {/* The one combination that silently stops all recording. */}
        {mode === 'allow' && list.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t.rules.emptyAllowWarning}
          </Alert>
        )}
        {mode === 'allow' && kind === 'user' && list.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t.rules.allowAnonWarning}
          </Alert>
        )}

        {mode !== 'off' && (
          <>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              <TextField
                size="small"
                label={kind === 'user' ? t.rules.addUser : t.rules.addIp}
                value={draft[kind].value}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [kind]: { ...d[kind], value: e.target.value } }))
                }
                sx={{ minWidth: 260 }}
              />
              <TextField
                size="small"
                label={t.rules.note}
                value={draft[kind].note}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [kind]: { ...d[kind], note: e.target.value } }))
                }
                sx={{ minWidth: 220 }}
              />
              <Button
                variant="contained"
                disabled={!draft[kind].value.trim() || addRule.isPending}
                onClick={() =>
                  addRule.mutate({
                    kind,
                    value: draft[kind].value.trim(),
                    note: draft[kind].note.trim() || undefined,
                  })
                }
              >
                {t.rules.add}
              </Button>
            </Stack>

            <List dense sx={{ mt: 1 }}>
              {list.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {t.rules.empty}
                </Typography>
              )}
              {list.map((r) => (
                <ListItem
                  key={r.id}
                  divider
                  secondaryAction={
                    <Tooltip title={t.rules.remove}>
                      <IconButton
                        size="small"
                        onClick={() => removeRule.mutate(r.id)}
                        disabled={removeRule.isPending}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={<Typography sx={{ fontFamily: 'monospace' }}>{r.value}</Typography>}
                    secondary={r.note ?? undefined}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <FiberManualRecordIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 820 }}>
        {t.subtitle}
      </Typography>

      {projects.length > 1 && (
        <TextField
          select
          size="small"
          label={t.project}
          value={projectId ?? ''}
          onChange={(e) => setProjectId(e.target.value)}
          sx={{ minWidth: 260, mb: 3 }}
        >
          {projects.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {(settings.isLoading || rules.isLoading) && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {s && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {t.sampling.heading}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
              {t.sampling.intro}
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
              {t.sampling.rate}
            </Typography>
            <Box sx={{ maxWidth: 420, px: 1 }}>
              <Slider
                value={Math.round(s.samplingRate * 100)}
                onChange={(_e, v) => patch.mutate({ samplingRate: (v as number) / 100 })}
                step={5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' },
                ]}
                min={0}
                max={100}
                valueLabelDisplay="on"
                valueLabelFormat={(v) => `${v}%`}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <TextField
              select
              size="small"
              label={t.sampling.mode}
              value={s.samplingMode}
              onChange={(e) => patch.mutate({ samplingMode: e.target.value as 'user' | 'session' })}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="user">{t.sampling.modeUser}</MenuItem>
              <MenuItem value="session">{t.sampling.modeSession}</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, maxWidth: 700 }}>
              {s.samplingMode === 'user' ? t.sampling.modeUserHelp : t.sampling.modeSessionHelp}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, maxWidth: 700 }}>
              {t.sampling.sdkNote}
            </Typography>
          </Paper>
        )}

        {s && ruleSection('user', t.rules.userHeading, t.rules.userIntro, s.userRuleMode)}
        {s && ruleSection('ip', t.rules.ipHeading, t.rules.ipIntro, s.ipRuleMode)}

        <Paper variant="outlined" sx={{ p: 3, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t.enforcement.heading}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 860 }}>
            {t.enforcement.body}
          </Typography>
        </Paper>
      </Stack>

      {selected && <Box sx={{ height: 8 }} />}
    </Box>
  );
}
